import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { adminActionLogs } from "../../api";

const actionLabels = {
  USER_STATUS_UPDATE: "유저 상태 변경",
  CAMPAIGN_HIDE: "캠페인 숨김",
  CAMPAIGN_SHOW: "캠페인 노출",
  CAMPAIGN_STATUS_UPDATE: "캠페인 상태 변경",
  ADMIN_PROPOSAL_CONFIRM: "크리에이터 확정",
  CONTENT_CONFIRM: "콘텐츠 승인",
  CONTENT_REJECT: "콘텐츠 반려",
  CONTENT_FEEDBACK: "콘텐츠 피드백",
  LIVE_SCHEDULE_UPDATE: "라이브 일정 변경",
};

const targetLabels = {
  USER: "유저",
  CAMPAIGN: "캠페인",
  PROPOSAL: "제안서",
  CONTENT: "콘텐츠",
  LIVE_SCHEDULE: "라이브 일정",
};

const emptyFilters = {
  actionType: "",
  targetType: "",
  targetId: "",
  adminUserId: "",
};

const fmtDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const prettyJson = (value) => {
  if (!value) return "-";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return String(value);
  }
};

export default function AuditLogsPage() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);

  const loadLogs = useCallback(
    async (nextPage, nextFilters) => {
      setLoading(true);
      try {
        const res = await adminActionLogs({
          page: nextPage ?? 1,
          size: 20,
          actionType: nextFilters?.actionType || undefined,
          targetType: nextFilters?.targetType || undefined,
          targetId: nextFilters?.targetId || undefined,
          adminUserId: nextFilters?.adminUserId || undefined,
        });
        const data = res?.data ?? res;
        setRows(Array.isArray(data?.contents) ? data.contents : []);
        setPageInfo({
          totalElements: data?.totalElements ?? 0,
          totalPages: data?.totalPages ?? 0,
          hasNext: data?.hasNext ?? false,
          hasPrevious: data?.hasPrevious ?? false,
        });
        setPage(nextPage ?? 1);
      } catch (e) {
        toast({
          status: "error",
          title: "감사 로그 조회 실패",
          description: e?.response?.data?.message ?? "잠시 후 다시 시도해주세요.",
        });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    void loadLogs(1, emptyFilters);
  }, [loadLogs]);

  const onFilterChange = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const applyFilters = () => {
    void loadLogs(1, filters);
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    void loadLogs(1, emptyFilters);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="24px">
        <Box>
          <Text fontSize="xl" fontWeight="bold" mb="2">
            감사 로그
          </Text>
          <Text color="gray.600">
            어드민이 수행한 계정, 캠페인, 콘텐츠 주요 변경 이력을 확인합니다.
          </Text>
        </Box>
        <Button onClick={() => void loadLogs(page, filters)} isLoading={loading}>
          새로고침
        </Button>
      </Flex>

      <Flex bg="white" p="16px" borderRadius="12px" gap="10px" mb="16px" wrap="wrap">
        <Select w="180px" value={filters.actionType} onChange={onFilterChange("actionType")}>
          <option value="">전체 액션</option>
          {Object.entries(actionLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Select w="160px" value={filters.targetType} onChange={onFilterChange("targetType")}>
          <option value="">전체 대상</option>
          {Object.entries(targetLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Input
          w="150px"
          value={filters.targetId}
          onChange={onFilterChange("targetId")}
          placeholder="대상 ID"
          inputMode="numeric"
        />
        <Input
          w="150px"
          value={filters.adminUserId}
          onChange={onFilterChange("adminUserId")}
          placeholder="어드민 ID"
          inputMode="numeric"
        />
        <Button colorScheme="blue" onClick={applyFilters}>
          검색
        </Button>
        <Button variant="outline" onClick={resetFilters}>
          초기화
        </Button>
      </Flex>

      <Box bg="white" borderRadius="12px" overflow="hidden" boxShadow="sm">
        <Table size="sm">
          <Thead bg="#E2EEFF">
            <Tr>
              <Th w="150px">일시</Th>
              <Th w="160px">액션</Th>
              <Th w="120px">대상</Th>
              <Th w="120px">어드민</Th>
              <Th>변경 전</Th>
              <Th>변경 후</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={6}>
                  <Flex justify="center" py="40px">
                    <Spinner />
                  </Flex>
                </Td>
              </Tr>
            ) : rows.length === 0 ? (
              <Tr>
                <Td colSpan={6}>
                  <Box py="40px" textAlign="center" color="gray.500">
                    조회된 감사 로그가 없습니다.
                  </Box>
                </Td>
              </Tr>
            ) : (
              rows.map((row, idx) => (
                <Tr key={`${row.createdAt}-${row.actionType}-${row.targetId}-${idx}`}>
                  <Td verticalAlign="top">{fmtDate(row.createdAt)}</Td>
                  <Td verticalAlign="top">
                    <VStack align="start" spacing="4px">
                      <Badge colorScheme="blue">
                        {actionLabels[row.actionType] ?? row.actionType}
                      </Badge>
                      <Text fontSize="12px" color="gray.500">
                        {row.actionType}
                      </Text>
                    </VStack>
                  </Td>
                  <Td verticalAlign="top">
                    <VStack align="start" spacing="4px">
                      <Text fontWeight="700">
                        {targetLabels[row.targetType] ?? row.targetType}
                      </Text>
                      <Text fontSize="12px" color="gray.500">
                        ID {row.targetId ?? "-"}
                        {row.targetSubId ? ` / ${row.targetSubId}` : ""}
                      </Text>
                    </VStack>
                  </Td>
                  <Td verticalAlign="top">
                    <Text>user #{row.adminUserId ?? "-"}</Text>
                    <Text fontSize="12px" color="gray.500">
                      profile #{row.adminProfileId ?? "-"}
                    </Text>
                  </Td>
                  <Td verticalAlign="top" maxW="280px">
                    <Code display="block" whiteSpace="pre-wrap" p="8px">
                      {prettyJson(row.beforeJson)}
                    </Code>
                  </Td>
                  <Td verticalAlign="top" maxW="280px">
                    <Code display="block" whiteSpace="pre-wrap" p="8px">
                      {prettyJson(row.afterJson)}
                    </Code>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <HStack justify="space-between" mt="16px">
        <Text color="gray.600" fontSize="14px">
          총 {pageInfo.totalElements.toLocaleString()}건
          {pageInfo.totalPages > 0 ? ` · ${page}/${pageInfo.totalPages}페이지` : ""}
        </Text>
        <HStack>
          <Button
            size="sm"
            variant="outline"
            isDisabled={!pageInfo.hasPrevious || loading}
            onClick={() => void loadLogs(Math.max(1, page - 1), filters)}
          >
            이전
          </Button>
          <Button
            size="sm"
            variant="outline"
            isDisabled={!pageInfo.hasNext || loading}
            onClick={() => void loadLogs(page + 1, filters)}
          >
            다음
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
}
